import { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { api, errMsg, media, GENRES, ROLES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const SOCIALS = [["instagram", "Instagram", "@handle or link"], ["spotify", "Spotify", "Artist link"], ["youtube", "YouTube", "@channel or link"], ["tiktok", "TikTok", "@handle or link"], ["soundcloud", "SoundCloud", "Profile link"], ["apple_music", "Apple Music", "Artist link"], ["bandcamp", "Bandcamp", "Page link"], ["website", "Website", "yoursite.com"]];
const field = "h-11 border-white/10 bg-[#07090E]";

const Select = ({ value, onChange, options, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${field} w-full rounded-md border px-3 text-sm capitalize`}>
    <option value="">Choose…</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const Profile = ({ d, reload }) => {
  const u = d.user;
  const [f, setF] = useState({ stage_name: u.stage_name || "", bio: u.bio || "", genre: u.genre || "", role_type: u.role_type || "", city: u.city || "", socials: u.socials || {} });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/me/profile", f); toast.success("Profile saved"); reload(); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.loading("Uploading photo…", { id: "photo" });
    try { await api.post("/me/photo", fd); toast.success("Photo updated", { id: "photo" }); reload(); } catch (er) { toast.error(errMsg(er), { id: "photo" }); }
  };

  return (
    <form onSubmit={save} className="grid gap-8 lg:grid-cols-12" data-testid="dash-profile">
      <div className="lg:col-span-4">
        <button type="button" onClick={() => fileRef.current.click()} data-testid="photo-upload-button" className="group relative block aspect-[4/5] w-full overflow-hidden rounded-2xl border border-dashed border-white/20 bg-[#0F1420]">
          {u.photo ? <img src={media(u.photo)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full flex-col items-center justify-center gap-3 text-slate-500"><Camera className="h-8 w-8" />Upload a photo</span>}
          <span className="absolute inset-x-0 bottom-0 bg-[#07090E]/80 py-3 text-center font-mono text-[11px] uppercase tracking-widest opacity-0 transition-opacity group-hover:opacity-100">Change photo</span>
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden data-testid="photo-file-input" onChange={(e) => upload(e.target.files[0])} />
        <p className="mt-2 text-xs text-slate-500">JPG, PNG or WEBP, max 8 MB. Portrait works best.</p>
      </div>
      <div className="space-y-5 lg:col-span-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <div><Label>Stage name</Label><Input data-testid="profile-stage-name" className={field} value={f.stage_name} onChange={set("stage_name")} /></div>
          <div><Label>City</Label><Input data-testid="profile-city" className={field} value={f.city} onChange={set("city")} /></div>
          <div><Label>Genre</Label><Select testid="profile-genre" value={f.genre} onChange={set("genre")} options={GENRES.map((g) => [g, g])} /></div>
          <div><Label>You are a…</Label><Select testid="profile-role" value={f.role_type} onChange={set("role_type")} options={ROLES} /></div>
        </div>
        <div>
          <Label>Bio — in your own words</Label>
          <Textarea data-testid="profile-bio" rows={6} maxLength={1500} className="border-white/10 bg-[#07090E]" value={f.bio} onChange={set("bio")} placeholder="Who you are, what your music sounds like, what's next." />
          <p className="mt-1 text-right font-mono text-[10px] text-slate-500">{f.bio.length}/1500</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {SOCIALS.map(([s, l, ph]) => (
            <div key={s}><Label>{l}</Label><Input data-testid={`profile-social-${s}`} className={field} placeholder={ph} value={f.socials[s] || ""} onChange={(e) => setF({ ...f, socials: { ...f.socials, [s]: e.target.value } })} /></div>
          ))}
        </div>
        <button className="btn-hot" disabled={busy} data-testid="profile-save-button">{busy ? "Saving…" : "Save profile"}</button>
      </div>
    </form>
  );
};
